import React, { useState, useEffect, useRef } from 'react';
import { getDocs, collection, doc, updateDoc, increment} from 'firebase/firestore';
import { db } from '../../../firebase';
import BlurhashImg from '../Image/BlurhashImg';
import { Link } from 'react-router-dom';
import { FaFacebook, FaTwitter, FaInstagram, FaPinterest, FaCopy } from 'react-icons/fa';
import './trending.css'

const incrementShareCount = async (postId) => {
  try {
    const postRef = doc(db, 'news', postId);
    await updateDoc(postRef, { 
      shareCount: increment(1) 
    });
  } catch (error) {
    console.error("Error incrementing share count:", error);
  }
};

const CTA = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePopupPostId, setActivePopupPostId] = useState(null);
  const [hoverPostId, setHoverPostId] = useState(null);
  const [localShareCounts, setLocalShareCounts] = useState({});
  const [copiedPostId, setCopiedPostId] = useState(null);
  const shareRefsMap = useRef({});

  // Fisher-Yates shuffle algorithm
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const postsCollection = collection(db, 'news');
        const snapshot = await getDocs(postsCollection);
        
        const fetchedPosts = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(post => post.category === "Architectural");

        // Shuffle the filtered posts instead of sorting by date
        const shuffledPosts = shuffleArray(fetchedPosts);
        
        console.log('Fetched and shuffled Architectural posts:', shuffledPosts);
        setPosts(shuffledPosts);
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setLoading(false);
      }
    };

    // Add click event listener to close popup when clicking outside
    const handleClickOutside = (event) => {
      let isClickInsideShareArea = false;
      
      // Check if the click is inside any of the share areas
      Object.values(shareRefsMap.current).forEach(ref => {
        if (ref && (ref.buttonRef.contains(event.target) || 
            (ref.popupRef && ref.popupRef.contains(event.target)))) {
          isClickInsideShareArea = true;
        }
      });

      if (!isClickInsideShareArea) {
        setActivePopupPostId(null);
        setHoverPostId(null);
        setCopiedPostId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    
    fetchPosts();

    // Cleanup event listener
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleShareButtonClick = (postId) => {
    // Toggle the popup
    setActivePopupPostId(prevId => prevId === postId ? null : postId);
    // Ensure hover is also set to allow persistent display
    setHoverPostId(postId);
  };

  const handleCopyLink = (postId) => {
    const articleUrl = `https://yourwebsite.com/article/${postId}`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(articleUrl).then(() => {
      // Set copied state and reset after 2 seconds
      setCopiedPostId(postId);
      setTimeout(() => setCopiedPostId(null), 2000);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  const handleSocialShare = async (postId, socialPlatform) => {
    try {
      // Increment local share count immediately
      setLocalShareCounts(prev => ({
        ...prev,
        [postId]: (prev[postId] || 0) + 1
      }));

      // Increment share count in Firestore
      await incrementShareCount(postId);

      // Open the share dialog for the specific social platform
      let shareUrl = '';
      const articleUrl = `https://yourwebsite.com/article/${postId}`;
      
      switch(socialPlatform) {
        case 'facebook':
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}`;
          break;
        case 'twitter':
          shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(articleUrl)}`;
          break;
        case 'instagram':
          // Note: Instagram doesn't allow direct sharing via web
          alert('Instagram sharing is not directly supported. Please copy the link.');
          return;
        case 'pinterest':
          shareUrl = `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(articleUrl)}`;
          break;
        default:
          return;
      }

      window.open(shareUrl, '_blank');
    } catch (error) {
      console.error("Error sharing post:", error);
    }
  };

  if (loading) {
    return <div>Loading articles...</div>;
  }

  if (posts.length === 0) {
    return (
      <div className="section__padding-paad">
        <h2 style={{
          fontFamily: "Poppins-SemiBold",
          color: "#CD3333",
          fontSize: "clamp(28px, 4vw, 36px)",
          fontWeight: "600"
        }}>
          Trending Topics
        </h2>
        <p>No Trending Topics found.</p>
      </div>
    );
  }

  const getFirstTextSection = (article) => {
    // If no sections exist, return first 250 chars of main content
    if (!article?.sections?.length) {
      const mainContent = article?.content || '';
      return mainContent.length > 120 ? mainContent.slice(0, 120) + '...' : mainContent;
    }
  
    // Find the first text section
    const firstTextSection = article.sections.find(section => section.type === 'text');
    
    // If no text section found, return empty string
    if (!firstTextSection?.content) return '';
    
    // Strip HTML tags but preserve line breaks and spaces
    const stripHtml = (html) => {
      // Replace line breaks with spaces
      const withLineBreaks = html.replace(/<br\s*\/?>/gi, ' ').replace(/<\/p>/gi, ' ');
      // Remove all HTML tags
      const withoutTags = withLineBreaks.replace(/<[^>]*>/g, '');
      // Remove multiple spaces and trim
      return withoutTags.replace(/\s+/g, ' ').trim();
    };
  
    // Get plain text content
    const plainText = stripHtml(firstTextSection.content);
    
    // Return first 250 chars
    return plainText.length > 120 ? plainText.slice(0, 120) + '...' : plainText;
  };
  

  return (
    <>
    <div className="section__padding-paad-trend">
      {/* <h2 style={{
        fontFamily: "Poppins-SemiBold",
        color: "#CD3333",
        fontSize: "clamp(28px, 4vw, 37px)",
        fontWeight: "600",
        marginBottom: "2rem",
        lineHeight: "1.2"
      }}>
        Trending Topics
      </h2> */}
  
      <div className="category-buttons-trend" style={{ display: "flex", flexDirection: "column", alignItems: "start" }}>
        <h1 className="" style={{ fontFamily: "Poppins-SemiBold", color: "#CD3333", fontSize: "clamp(28px, 4vw, 37px)", fontWeight: "600" }}>Trending Topics</h1>
          <p className="width-p_trend">
            Designed to the exacting European Designed to the exacting European Passive House Standard, this is architect Cedric Burgers’s family home, Passive House Standard, this is architect Cedric Burgers’s family home,
          </p>
      </div>

      {/* <div className="w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"> */}
      <div className="works-container-trend">
        <div className="work-cards-trend">
          {posts.map((post) => (
            <Link 
              to={`/article/${post.id}`} 
              key={post.id} 
              className="mb-4 cursor-pointer duration-300"
            >
              <div>
                {/* <div className="h-[250px] md:h-[350px] w-full overflow-hidden"> */}
                <div className="relative aspect-[16/9] w-full overflow-hidden">
                  <BlurhashImg
                    src={post.coverImage}
                    alt={post.title}
                    className="w-full h-full object-cover responsive blur_load"
                    id={`clickableImage${post.id}`}
                    loading="lazy" 
                    placeholderColor="#888"
                    onError={(e) => {
                      console.error('Error loading image:', post.coverImage);
                      e.target.src = '/fallback-image.jpg';
                    }}
                  />
                </div>
                <div className="mt-4">
                  <p style={{
                    fontFamily: "Poppins-Regular",
                    color: "#CD3333",
                    fontSize: "16px"
                  }}>
                    {post.category}
                  </p>
                  <h3 style={{
                    fontFamily: "Poppins-SemiBold",
                    fontSize: "26px",
                    fontWeight: "600",
                    lineHeight: "1.2",
                    paddingTop: '6px'
                  }}>
                    {post.title}
                  </h3>
                  <div 
                    style={{
                      fontFamily: "Poppins-Light",
                      lineHeight: "1.3",
                      color: "#000000",
                      fontSize: '14px',
                      paddingTop: '6px'
                    }}
                    dangerouslySetInnerHTML={{ 
                      __html: (() => {
                        const content = getFirstTextSection(post);
                        if (!content) return 'No content available';
                        return content.length > 250 ? content.slice(0, 250) + '...' : content;
                      })()
                    }} 
                  />
                </div>
                <div 
                  className="flex items-center mt-2 relative"
                  onMouseEnter={() => setHoverPostId(post.id)}
                  onMouseLeave={() => {
                    // Only remove hover if not actively clicked
                    if (activePopupPostId !== post.id) {
                      setHoverPostId(null);
                    }
                  }}
                >
                  <p style={{
                    fontFamily: "Poppins-Light",
                    fontSize: "16px",
                    color: "#CD3333",
                  }}>
                    {new Date(post.createdAt?.seconds * 1000).toLocaleDateString()}
                  </p>

                  <span className="ml-12" style={{color: "#CD3333"}}>
                    {/* {localShareCounts[post.id] || post.shareCount || 0} */}
                  </span>
                  <button 
                    ref={(el) => {
                      if (el) {
                        if (!shareRefsMap.current[post.id]) {
                          shareRefsMap.current[post.id] = {};
                        }
                        shareRefsMap.current[post.id].buttonRef = el;
                      }
                    }}
                    className="share-button ml-2"
                    onClick={(e) => {
                      e.stopPropagation(); 
                      e.preventDefault(); 
                      handleShareButtonClick(post.id);
                    }}
                    style={{color: "#CD3333"}}
                  >
                    Share
                  </button>

                  {(hoverPostId === post.id || activePopupPostId === post.id) && (
                    <div 
                      ref={(el) => {
                        if (el) {
                          if (!shareRefsMap.current[post.id]) {
                            shareRefsMap.current[post.id] = {};
                          }
                          shareRefsMap.current[post.id].popupRef = el;
                        }
                      }}
                      className="social-popup absolute z-10 bg-white border border-gray-200 p-3 rounded shadow-lg"
                      onClick={(e) => e.stopPropagation()} 
                    >
                      <a
                        href="#"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="issue-details__social-link-feed"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSocialShare(post.id, 'facebook');
                        }}
                      >
                        <FaFacebook />
                      </a>
                      <a
                        href="#"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="issue-details__social-link-feed"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSocialShare(post.id, 'twitter');
                        }}
                      >
                        <FaTwitter />
                      </a>
                      <a
                        href="#"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="issue-details__social-link-feed"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSocialShare(post.id, 'instagram');
                        }}
                      >
                        <FaInstagram />
                      </a>
                      <a
                        href="#"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="issue-details__social-link-feed"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSocialShare(post.id, 'pinterest');
                        }}
                      >
                        <FaPinterest />
                      </a>
                      <a
                        href="#"
                        className={`issue-details__social-link-feed ${
                          copiedPostId === post.id ? 'text-green-500' : ''
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCopyLink(post.id);
                        }}
                        title={copiedPostId === post.id ? 'Copied!' : 'Copy Link'}
                      >
                        {copiedPostId === post.id ? '✓ Copied' : <FaCopy />}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
    <div style={{ maxHeight: '750px', overflowY: 'auto', borderTop: "1px solid #000", margin: "2rem 2rem 0rem 2rem" }}></div>
    </>
  );
};

export default CTA;